import api from '../../api'
import { canEditResource } from './permissions'
import type { DragPayload } from '../../features/workspace/sidebar/SideBarTypes'

export type DragMoveParams = {
    clientId: string | null;
    memberRole?: string;
    memberCanCreate?: boolean;
    roomCreatorClientId?: string;
    membersList: { clientId: string; role: string }[];
};

// Same edit-permission rule as canEditResource, plus: a plain member without
// canCreate can't drag anything (including their own resources) — mirrors
// canDragSnip/canDragFile/canDragFolder in FilesTab.tsx.
export const canDragResource = (creatorId: string, params: DragMoveParams): boolean => {
    if (!params.clientId) return false;
    if (params.memberCanCreate !== true && params.memberRole === 'member') return false;
    return canEditResource({
        creatorId,
        clientId: params.clientId,
        memberRole: params.memberRole,
        roomCreatorClientId: params.roomCreatorClientId,
        membersList: params.membersList,
    });
};

export const canDropIntoFolder = (
    targetFolder: { creatorId: string; folderPermission?: string | null } | null,
    itemCreatorId: string,
    params: DragMoveParams
): boolean => {
    if (!targetFolder) return false;
    if (!canDragResource(itemCreatorId, params)) return false;

    if (params.memberRole === 'owner' || params.memberRole === 'admin') return true;
    if (targetFolder.creatorId === params.clientId) return true;
    return (targetFolder.folderPermission ?? 'restricted') === 'open';
};

// Single generic move: pass targetFolderId to move into a folder, or null to
// move to the room root. Consolidates the six near-identical
// moveXToFolder/moveXToMain wrappers in FilesTab.tsx into one call.
export const moveResourceTo = async (
    roomId: string,
    item: DragPayload,
    targetFolderId: string | null
): Promise<void> => {
    if (targetFolderId) {
        if (item.kind === 'snippet') {
            await api.patch(`/rooms/${roomId}/folders/${targetFolderId}/addsnippet`, { snippetId: item.id });
        } else if (item.kind === 'file') {
            await api.patch(`/rooms/${roomId}/folders/${targetFolderId}/files`, { fileId: item.id });
        } else {
            await api.patch(`/rooms/${roomId}/folders/${targetFolderId}/addFolder`, { folderId: item.id });
        }
        return;
    }

    if (item.kind === 'snippet') {
        await api.patch(`/rooms/${roomId}/snippets/${item.id}/move`);
    } else if (item.kind === 'file') {
        await api.patch(`/rooms/${roomId}/files/${item.id}/move`);
    } else {
        await api.patch(`/rooms/${roomId}/folders/${item.id}/move`);
    }
};

// Same MIME key FilesTab.tsx's sidebar tree drag-and-drop uses, so a drag
// started in either place is readable by the other.
export const DRAG_MIME = 'application/x-flashroom-dnd';
